import Button from '@/src/components/atom/Button';
import LucideIcon from '@/src/components/atom/Icons/LucideIcon';
import BottomFixedContainer from '@/src/components/molecule/BottomFixedContainer';
import MainContentTitle from '@/src/components/molecule/MainContentTitle';
import PATH from '@/src/routes/path';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { DetailSctionRenderer } from '@/src/components/organism/DetailSection/renderer';
import DetailSection from '@/src/components/organism/DetailSection';
import AlertDialog from '@/src/components/atom/AlertDialog';
import useModalState from '@/src/hooks/useModalState';
import { toast } from '@/src/hooks/useToast';
import {
  deleteHomeContentAPI,
  getHomeContentByIAPI,
  getHomeContentCatetoryAPI,
  getHomeContentInAppLinkCatetoryAPI,
} from '@/src/apis/rest/homeContent';

type ModalState = 'NONE' | 'DELETE';

const LIST_PATH = PATH.APP_HOME_CONTENT_LIST;
const BACK_PATH = PATH.APP_HOME_CONTENT_LIST;
const EDIT_PATH = PATH.APP_HOME_CONTENT_EDIT;

export default function AppHomeContentDetailPage() {
  // util
  const navigate = useNavigate();
  const { id } = useParams();

  // state
  const { modalState, handleOpenModal, handleToggleModal } =
    useModalState<ModalState>();

  // query
  const { data: contentCategoryOptionsObject } = useQuery({
    queryKey: ['home-content-category-options'],
    queryFn: getHomeContentCatetoryAPI,
    select: data =>
      (data?.rows || []).reduce(
        (acc, cur) => {
          return { ...acc, [cur.value]: cur.key };
        },
        {} as Record<string, string>,
      ),
  });

  const { data: contentInAppLinkCategoryOptionsObject } = useQuery({
    queryKey: ['home-content-in-app-link-category-options'],
    queryFn: getHomeContentInAppLinkCatetoryAPI,
    select: data =>
      (data?.rows || []).reduce(
        (acc, cur) => {
          return { ...acc, [cur.value]: cur.key };
        },
        {} as Record<string, string>,
      ),
  });
  const { data: homeContentData } = useQuery({
    queryKey: ['home-content', id],
    queryFn: () => getHomeContentByIAPI({ id: Number(id) }),
    meta: {
      backUrl: BACK_PATH,
      callback: () => {
        navigate(BACK_PATH, { replace: true });
      },
    },
  });

  // mutation
  const deleteHomeContentMutation = useMutation({
    mutationFn: deleteHomeContentAPI,
  });

  // event
  const handleGoBack = () => {
    navigate(-1);
  };
  const handleDeleteHomeContent = () => {
    deleteHomeContentMutation.mutate(
      { id: Number(id) },
      {
        onSuccess: () => {
          toast({
            variant: 'success',
            title: '성공',
            description: '성공적으로 삭제되었습니다.',
          });
          setTimeout(() => {
            navigate(LIST_PATH, { replace: true });
          }, 0);
        },
        onError: e => {
          toast({
            variant: 'error',
            title: '실패',
            description: e.message,
          });
        },
      },
    );
  };

  // render
  const renderBottomFixedContent = () => {
    return (
      <BottomFixedContainer>
        <div className="flex justify-between">
          <Button
            className="w-max"
            buttonSize="medium"
            buttonColor="outline"
            onClick={handleGoBack}
          >
            <div className="flex items-center gap-x-2">
              <LucideIcon name="ChevronLeft" size={16} />
              이전 화면
            </div>
          </Button>
          <div className="flex items-center gap-2">
            <Button
              className="w-40"
              buttonSize="medium"
              buttonColor="red"
              onClick={() => handleOpenModal('DELETE')}
              isLoading={deleteHomeContentMutation.isPending}
            >
              삭제
            </Button>
            <Link to={EDIT_PATH.replace(':id', id || '')}>
              <Button className="w-40" buttonSize="medium">
                수정
              </Button>
            </Link>
          </div>
        </div>
      </BottomFixedContainer>
    );
  };

  return (
    <>
      <MainContentTitle
        breadcrumbList={['홈 퀵메뉴 관리', '홈 퀵메뉴 상세']}
        pageTitle="홈 퀵메뉴 상세"
      />
      <div className="mb-[100px] flex flex-col gap-4">
        <DetailSection
          sectionTitle={
            <div className="flex w-full items-center justify-between">
              <span className="font-pretendardSemiBold text-b18sb">
                홈 퀵메뉴 정보
              </span>
            </div>
          }
          data={homeContentData}
          contents={[
            [
              {
                key: 'title',
                lable: '제목',
              },
              {
                key: 'isExposed',
                lable: '노출 여부',
                valueSetter: data => (data?.isExposed ? 'true' : 'false'),
                render: DetailSctionRenderer.BADGE({
                  options: [
                    {
                      label: '노출',
                      key: 'true',
                      badgeColor: 'lightGreen',
                    },
                    {
                      label: '미노출',
                      key: 'false',
                      badgeColor: 'lightGray',
                    },
                  ],
                }),
              },
              {
                key: 'contentMedia',
                lable: '아이콘 이미지',
                valueSetter: data => data?.contentMedia?.bufferFullUrl || '',
                render: DetailSctionRenderer.IMAGE({ width: 140, height: 140 }),
              },
            ],
            [
              {
                key: 'isExposeNewBadge',
                lable: '신규 뱃지 알림 여부',
                valueSetter: data => {
                  return data?.isExposeNewBadge ? 'true' : 'false';
                },
                render: DetailSctionRenderer.BADGE({
                  options: [
                    {
                      label: '노출',
                      key: 'true',
                      badgeColor: 'lightGreen',
                    },
                    {
                      label: '미노출',
                      key: 'false',
                      badgeColor: 'lightGray',
                    },
                  ],
                }),
              },
              {
                key: 'contentType',
                lable: '컨텐츠 유형',
                valueSetter: data =>
                  contentCategoryOptionsObject?.[data?.contentType as string] ||
                  '-',
              },
              {
                key: 'content',
                lable: '연결 컨텐츠',
                valueSetter: data => {
                  return (
                    contentInAppLinkCategoryOptionsObject?.[
                      data?.content as string
                    ] ||
                    data?.content ||
                    '-'
                  );
                },
                render: (value: string) =>
                  value.includes('https://') || value.includes('http://') ? (
                    <a
                      className="text-b16r text-gray6 underline"
                      href={value}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {value}
                    </a>
                  ) : (
                    value
                  ),
              },
              {
                key: 'createdAt',
                lable: '등록 일시',
                render: DetailSctionRenderer.DATE({
                  format: 'YYYY-MM-DD HH:mm:ss',
                }),
              },
              {
                key: ['administrator', 'name'],
                lable: '등록 관리자',
              },
            ],
          ]}
        />
      </div>
      {renderBottomFixedContent()}

      {/* 모달 */}
      {modalState === 'DELETE' && (
        <AlertDialog
          open={modalState === 'DELETE'}
          onOpenChange={() => handleToggleModal('DELETE')}
          okText="확인"
          onOk={handleDeleteHomeContent}
          title="컨텐츠 삭제"
          okButtonProps={{
            isLoading: deleteHomeContentMutation.isPending,
          }}
        >
          <p className="whitespace-pre-wrap">
            컨텐츠를 삭제하시겠습니까?
            <br />
            삭제된 컨텐츠는 사용자에게 노출되지 않습니다.
          </p>
        </AlertDialog>
      )}
    </>
  );
}
